import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, authApi } from "@/lib/api";

const AuthContext = createContext(null);

// Interceptor: al detectar 401, intentamos refresh una vez; si falla, limpiamos sesión.
let refreshInFlight = null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // axios withCredentials ya viene habilitado globalmente
    const interceptorId = api.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && !original._retry && !original.url?.includes("/auth/")) {
          original._retry = true;
          try {
            refreshInFlight = refreshInFlight || authApi.refresh();
            await refreshInFlight;
            refreshInFlight = null;
            return api(original);
          } catch {
            refreshInFlight = null;
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    hydrate();
    return () => api.interceptors.response.eject(interceptorId);
  }, [hydrate]);

  const login = async (email, password, remember = false) => {
    const u = await authApi.login(email, password, remember);
    setUser(u);
    return u;
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: hydrate }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
